/**
 * Onsite workspaces: register one per client GCP project, queue a Terraform plan, read it, and
 * approve its apply. The agent inside the client project pulls the work. Below them, the agent's
 * releases. Owns its own `QueryClientProvider`, as the Dottie context page does.
 */
import { Button, FormError, QueryView } from "@simplicity/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { CreateWorkspaceForm } from "./CreateWorkspaceForm";
import { ReleasesSection } from "./ReleasesSection";
import type { OnsiteWorkspace } from "./types";
import { useWorkspaces } from "./useOnsite";
import { WorkspaceDetail } from "./WorkspaceDetail";
import { WorkspacesTable } from "./WorkspacesTable";

function Workspaces({
  workspaces,
  selectedId,
  onSelect,
}: {
  workspaces: OnsiteWorkspace[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const selected = workspaces.find((w) => w.id === selectedId) ?? null;
  return (
    <div className="space-y-10">
      <WorkspacesTable workspaces={workspaces} selectedId={selectedId} onSelect={onSelect} />
      {selected ? (
        <WorkspaceDetail key={selected.id} workspace={selected} />
      ) : (
        workspaces.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Select a workspace to request a plan or review its runs.
          </p>
        )
      )}
    </div>
  );
}

function PageBody() {
  const workspacesQuery = useWorkspaces();
  const [searchParams, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const selectedId = searchParams.get("workspace");

  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("workspace", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl text-foreground">Onsite</h1>
          <p className="text-sm text-muted-foreground">
            The agent in each client project checks in on a schedule, runs queued plans and approved
            applies, and reports back here.
          </p>
        </div>
        {!creating && (
          <Button variant="outline" onClick={() => setCreating(true)}>
            Register workspace
          </Button>
        )}
      </header>
      {creating && (
        <CreateWorkspaceForm
          onCancel={() => setCreating(false)}
          onCreated={(workspace) => {
            setCreating(false);
            select(workspace.id);
          }}
        />
      )}
      <QueryView
        query={workspacesQuery}
        loading={<div className="empty-state">Loading workspaces...</div>}
        error={(err) => <FormError assertive>Failed to load workspaces: {err.message}</FormError>}
      >
        {(workspaces) => (
          <Workspaces workspaces={workspaces} selectedId={selectedId} onSelect={select} />
        )}
      </QueryView>
      <ReleasesSection />
    </div>
  );
}

export function OnsitePage() {
  const client = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } }),
    [],
  );
  return (
    <QueryClientProvider client={client}>
      <PageBody />
    </QueryClientProvider>
  );
}
